import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronsUpDown, Plus, X } from "lucide-react";
import {
  Button,
  Checkbox,
  Popover,
  PopoverContent,
  PopoverTrigger,
  cn,
} from "@nexus/ui";
import type { OrgPickerItem } from "./OrgPickerCombobox";
import {
  ORG_PICKER_ITEM_CLASS,
  ORG_PICKER_POPOVER_CLASS,
  OrgPickerOptionContent,
  OrgTypeBadge,
  orgPickerItemStyle,
} from "./org-picker-shared";
import { OrgLogoMark } from "./OrgLogoMark";
import { tenantAccentColor } from "@/features/identity-hub/utils/iam.utils";
import { orgAccentRowSurfaceStyle } from "@/features/identity-hub/utils/org-accent-surface";

interface OrgMultiPickerProps {
  items: OrgPickerItem[];
  value: string[];
  onValueChange: (ids: string[]) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  disabled?: boolean;
  className?: string;
  ariaLabel?: string;
}

function OrgSelectionBadge({
  org,
  onRemove,
  disabled,
}: {
  org: OrgPickerItem;
  onRemove: () => void;
  disabled?: boolean;
}) {
  const accent = tenantAccentColor(org);

  return (
    <span
      className="relative inline-flex max-w-full items-center gap-1.5 overflow-hidden rounded-md border py-0.5 pl-1.5 pr-1"
      style={orgAccentRowSurfaceStyle(accent)}
    >
      <OrgLogoMark
        name={org.name}
        logoUrl={org.logoUrl}
        accent={accent}
        isSuperTenant={org.type === "SUPER_TENANT"}
        size={16}
      />
      <span className="max-w-[8rem] truncate text-[12px] text-foreground">{org.name}</span>
      <OrgTypeBadge type={org.type} />
      <button
        type="button"
        disabled={disabled}
        aria-label={`Remove ${org.name}`}
        onClick={onRemove}
        className="ml-0.5 rounded p-0.5 text-muted-foreground transition-colors hover:bg-muted/80 hover:text-foreground disabled:pointer-events-none disabled:opacity-40"
      >
        <X className="size-3" aria-hidden />
      </button>
    </span>
  );
}

export function OrgMultiPicker({
  items,
  value,
  onValueChange,
  placeholder = "Add organizations…",
  searchPlaceholder = "Search organizations…",
  disabled = false,
  className,
  ariaLabel,
}: OrgMultiPickerProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [pending, setPending] = useState<Set<string>>(new Set());

  const itemsById = useMemo(() => new Map(items.map((org) => [org.id, org])), [items]);
  const selectedOrgs = useMemo(
    () => value.map((id) => itemsById.get(id)).filter((org): org is OrgPickerItem => !!org),
    [value, itemsById],
  );

  const availableItems = useMemo(
    () => items.filter((org) => !value.includes(org.id)),
    [items, value],
  );

  const filteredItems = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return availableItems;
    return availableItems.filter((org) => org.name.toLowerCase().includes(q));
  }, [availableItems, query]);

  const pendingCount = pending.size;

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next) {
      setQuery("");
      setPending(new Set());
    }
  };

  const togglePending = (orgId: string) => {
    setPending((prev) => {
      const next = new Set(prev);
      if (next.has(orgId)) next.delete(orgId);
      else next.add(orgId);
      return next;
    });
  };

  const handleSelectAll = () => {
    setPending(new Set(filteredItems.map((org) => org.id)));
  };

  const handleClearPending = () => {
    setPending(new Set());
  };

  const handleAdd = () => {
    if (pendingCount === 0) return;
    onValueChange([...value, ...pending]);
    setPending(new Set());
    setQuery("");
    setOpen(false);
  };

  const handleRemove = (orgId: string) => {
    onValueChange(value.filter((id) => id !== orgId));
  };

  return (
    <div className={cn("flex flex-col gap-2", className)}>
      {selectedOrgs.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {selectedOrgs.map((org) => (
            <OrgSelectionBadge
              key={org.id}
              org={org}
              disabled={disabled}
              onRemove={() => handleRemove(org.id)}
            />
          ))}
        </div>
      )}

      <Popover open={open} onOpenChange={handleOpenChange}>
        <PopoverTrigger asChild>
          <button
            type="button"
            role="combobox"
            aria-expanded={open}
            aria-label={ariaLabel}
            disabled={disabled || availableItems.length === 0}
            className={cn(
              "flex h-9 w-full items-center gap-2 rounded-md border border-border bg-background px-3 text-sm outline-none",
              "focus:border-ring focus:ring-1 focus:ring-ring/40",
              open && "border-ring ring-1 ring-ring/40",
              "disabled:cursor-not-allowed disabled:opacity-50",
            )}
          >
            <span className="min-w-0 flex-1 truncate text-left text-muted-foreground">
              {availableItems.length === 0 ? t("iam.personDrawer.orgs.allOrgsSelected", "All organizations selected") : placeholder}
            </span>
            <ChevronsUpDown className="size-3.5 shrink-0 opacity-50" aria-hidden />
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" sideOffset={4} className={ORG_PICKER_POPOVER_CLASS}>
          <div className="flex items-center gap-2 border-b border-border/70 px-3 py-2">
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={searchPlaceholder}
              className="h-8 min-w-0 flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
            />
          </div>

          <div className="flex items-center justify-between gap-2 border-b border-border/50 px-3 py-1.5">
            <span className="text-[11px] text-muted-foreground">
              {t("iam.personDrawer.orgs.nOfMSelected", "{{count}} of {{total}} selected", { count: pendingCount, total: filteredItems.length })}
            </span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                disabled={filteredItems.length === 0}
                onClick={handleSelectAll}
                className="text-[11px] font-medium text-primary hover:underline disabled:pointer-events-none disabled:opacity-40"
              >
                {t("iam.personDrawer.orgs.addAll", "Add all")}
              </button>
              <button
                type="button"
                disabled={pendingCount === 0}
                onClick={handleClearPending}
                className="text-[11px] font-medium text-muted-foreground hover:text-foreground disabled:pointer-events-none disabled:opacity-40"
              >
                {t("iam.personDrawer.orgs.unselectAll", "Unselect all")}
              </button>
            </div>
          </div>

          <div
            className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-1.5"
            onWheel={(event) => event.stopPropagation()}
          >
            {filteredItems.length === 0 ? (
              <p className="px-2 py-6 text-center text-sm text-muted-foreground">{t("iam.personDrawer.orgs.noOrgsFound", "No organizations found.")}</p>
            ) : (
              filteredItems.map((org) => {
                const isPending = pending.has(org.id);
                return (
                  <div
                    key={org.id}
                    role="option"
                    aria-selected={isPending}
                    onClick={() => togglePending(org.id)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        togglePending(org.id);
                      }
                    }}
                    tabIndex={0}
                    data-selected={isPending}
                    className={cn(
                      "relative flex w-full cursor-pointer items-center gap-2 rounded-sm px-2 py-2 text-left outline-none focus-visible:ring-1 focus-visible:ring-ring/40",
                      ORG_PICKER_ITEM_CLASS,
                    )}
                    style={orgPickerItemStyle(org)}
                  >
                    <Checkbox
                      checked={isPending}
                      tabIndex={-1}
                      aria-hidden
                      className="relative z-[1] shrink-0 pointer-events-none"
                    />
                    <OrgPickerOptionContent org={org} />
                  </div>
                );
              })
            )}
          </div>

          <div className="border-t border-border/70 p-2">
            <Button
              type="button"
              size="sm"
              className="w-full"
              disabled={pendingCount === 0}
              onClick={handleAdd}
            >
              <Plus className="size-3.5" aria-hidden />
              {pendingCount > 0
                ? t("iam.personDrawer.orgs.addOrgsCta", { count: pendingCount })
                : t("iam.personDrawer.orgs.addOrgsCta_zero", "Add organizations")}
            </Button>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
